import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import LoadingSpinner from '../common/LoadingSpinner';
import SessionExpiredModal from '../common/SessionExpiredModal';
import BookingTimer from './hotel-details/BookingTimer';
import { useBookingSession } from '../../hooks/useBookingSession';
import agentApiService from '../../services/agentApiService';

const AgentCustomerDetailsPage = () => {
  const { hotelId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // URL parameters
  const roomBookingCode = searchParams.get('roomBookingCode');
  const optionId = searchParams.get('optionId');
  const destination = searchParams.get('destination');
  const checkInDate = searchParams.get('checkInDate');
  const checkOutDate = searchParams.get('checkOutDate');
  const roomAllocations = searchParams.get('roomAllocations');
  const provider = searchParams.get('provider');
  const timerKey = searchParams.get('timerKey');
  const reserveCode1 = searchParams.get('reserveCode');

  // State
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [customerData, setCustomerData] = useState(null);
  const [timeoutProgress, setTimeoutProgress] = useState(0);
  const [showTimeoutWarning, setShowTimeoutWarning] = useState(false);
  const [passengerDetails, setPassengerDetails] = useState({
    rooms: [],
    gstInfo: {
      gstNumber: '',
      gstCompanyName: '',
      gstCompanyAddress: '',
      gstCompanyContactNumber: '',
      gstCompanyEmail: ''
    }
  });
  const [commonInfo, setCommonInfo] = useState({
    syncCommonInfo: false,
    syncPanInfo: false,
    mobile: '',
    email: '',
    country: '',
    pan: ''
  });
  const [specialRequests, setSpecialRequests] = useState({
    requestText: '',
    closeRooms: false,
    freeParking: false
  });
  const [arrivalInfo, setArrivalInfo] = useState({
    arrivalDate: '',
    arrivalTime: ''
  });
  const [showSessionExpiredModal, setShowSessionExpiredModal] = useState(false);

  // Use timerKey as expiryTime (timerKey contains the expiry timestamp)
  const expiryTime = timerKey ? parseInt(timerKey, 10) : null;
  const { isExpired, hasValidSession, getTimeRemaining } = useBookingSession(
    expiryTime,
    () => setShowSessionExpiredModal(true) // Show modal when session expires
  );

  // Fetch customer detail data on component mount
  useEffect(() => {
    console.log('AgentCustomerDetailsPage: Component mounted');
    console.log('agentApiService type:', typeof agentApiService);
    console.log('getCustomerDetail method type:', typeof agentApiService.getCustomerDetail);
    
    if (typeof agentApiService.getCustomerDetail !== 'function') {
      console.error('getCustomerDetail is not a function!');
      console.error('Available properties:', Object.keys(agentApiService));
      console.error('Available methods:', Object.getOwnPropertyNames(Object.getPrototypeOf(agentApiService)));
      return;
    }
    
    console.log('getCustomerDetail method is available, calling fetchCustomerData...');
    fetchCustomerData();
  }, []);

  const fetchCustomerData = async () => {
    try {
      setLoading(true);
      setError(null);
      setTimeoutProgress(0);
      setShowTimeoutWarning(false);

      const params = new URLSearchParams({
        roomBookingCode: roomBookingCode || '',
        optionId: optionId || '',
        hotelId: hotelId || '',
        destination: destination || '',
        checkInDate: checkInDate || '',
        checkOutDate: checkOutDate || '',
        roomAllocations: roomAllocations || '',
        provider: provider || '',
        timerKey: timerKey || ''
      });

      if (reserveCode1) {
        params.append('reserveCode1', reserveCode1);
      }

      // Start timeout progress indicator
      const progressInterval = setInterval(() => {
        setTimeoutProgress(prev => {
          const newProgress = prev + (100 / 30); // 30 seconds total
          if (newProgress >= 50) { // Show warning at 15 seconds (50% progress)
            setShowTimeoutWarning(true);
          }
          return Math.min(newProgress, 100);
        });
      }, 1000);

      console.log('Calling agentApiService.getCustomerDetail with params:', params.toString());
      const response = await agentApiService.getCustomerDetail(params.toString());

      // Clear progress interval
      clearInterval(progressInterval);
      setTimeoutProgress(0);
      setShowTimeoutWarning(false);

      if (response.success) {
        setCustomerData(response);
        initializePassengerDetails(response.hotelDetails);
      } else {
        throw new Error(response.message || 'Failed to fetch customer data');
      }
    } catch (error) {
      console.error('Error fetching customer data:', error);
      setError(error.message);
      setTimeoutProgress(0);
      setShowTimeoutWarning(false);
      toast.error(`Failed to load customer data: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };
  const initializePassengerDetails = (hotelDetails) => {
    if (!hotelDetails || !hotelDetails.rooms) return;

    const rooms = hotelDetails.rooms.map((room, roomIndex) => ({
      roomNumber: room.roomNumber,
      adults: Array.from({ length: room.adults }, () => ({
        passengerTitle: 'Mr.',
        passengerFirstName: '',
        passengerLastName: '',
        passengerPANCardNumber: '',
        passengerPhoneNumber: '',
        passengerEmail: '',
        passengerCountry: '',
        passengerPassportNo: '',
        passengerPassportIssueDate: '',
        passengerPassportExpDate: ''
      })),
      children: room.childAges ? room.childAges.map((age, childIndex) => ({
        passengerTitle: 'Master',
        passengerFirstName: '',
        passengerLastName: '',
        passengerAge: age,
        passengerPassportNo: '',
        passengerPassportIssueDate: '',
        passengerPassportExpDate: ''
      })) : []
    }));

    setPassengerDetails(prev => ({
      ...prev,
      rooms
    }));
  };

  const handleInputChange = (roomIndex, passengerType, passengerIndex, field, value) => {
    setPassengerDetails(prev => {
      const newDetails = { ...prev };
      newDetails.rooms[roomIndex][passengerType][passengerIndex][field] = value;
      return newDetails;
    });
  };

  const handleGstChange = (field, value) => {
    setPassengerDetails(prev => ({
      ...prev,
      gstInfo: {
        ...prev.gstInfo,
        [field]: value
      }
    }));
  };
  const handleCommonInfoChange = (field, value) => {
    setCommonInfo(prev => ({
      ...prev,
      [field]: value
    }));

    // Auto-fill when sync is enabled
    if (commonInfo.syncCommonInfo && ['mobile', 'email', 'country'].includes(field)) {
      applyCommonInfo(field, value);
    }
    if (commonInfo.syncPanInfo && field === 'pan') {
      applyPanInfo(value);
    }
  };

  const applyCommonInfo = (field, value) => {
    setPassengerDetails(prev => {
      const newDetails = { ...prev };
      newDetails.rooms.forEach(room => {
        room.adults.forEach(adult => {
          if (field === 'mobile') adult.passengerPhoneNumber = value;
          if (field === 'email') adult.passengerEmail = value;
          if (field === 'country') adult.passengerCountry = value;
        });
      });
      return newDetails;
    });
  };

  const applyPanInfo = (value) => {
    setPassengerDetails(prev => {
      const newDetails = { ...prev };
      newDetails.rooms.forEach(room => {
        room.adults.forEach(adult => {
          adult.passengerPANCardNumber = value;
        });
      });
      return newDetails;
    });
  };

  const handleSpecialRequestChange = (field, value) => {
    setSpecialRequests(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleArrivalInfoChange = (field, value) => {
    setArrivalInfo(prev => ({
      ...prev,
      [field]: value
    }));
  };
  const validateForm = () => {
    const errors = [];

    passengerDetails.rooms.forEach((room, roomIndex) => {
      // Validate adults
      room.adults.forEach((adult, adultIndex) => {
        const prefix = `Room ${roomIndex + 1} Adult ${adultIndex + 1}`;
        if (!adult.passengerFirstName.trim()) errors.push(`${prefix}: First name is required`);
        if (!adult.passengerLastName.trim()) errors.push(`${prefix}: Last name is required`);
        if (!adult.passengerPhoneNumber.trim()) errors.push(`${prefix}: Phone number is required`);
        if (!adult.passengerCountry.trim()) errors.push(`${prefix}: Country is required`);
      });

      // Validate children
      room.children.forEach((child, childIndex) => {
        const prefix = `Room ${roomIndex + 1} Child ${childIndex + 1}`;
        if (!child.passengerFirstName.trim()) errors.push(`${prefix}: First name is required`);
        if (!child.passengerLastName.trim()) errors.push(`${prefix}: Last name is required`);
      });
    });

    // Check for duplicate names
    const allNames = new Set();
    let hasDuplicates = false;

    passengerDetails.rooms.forEach(room => {
      [...room.adults, ...room.children].forEach(passenger => {
        if (passenger.passengerFirstName && passenger.passengerLastName) {
          const fullName = `${passenger.passengerFirstName} ${passenger.passengerLastName}`.toLowerCase().trim();
          if (allNames.has(fullName)) {
            hasDuplicates = true;
          } else {
            allNames.add(fullName);
          }
        }
      });
    });

    if (hasDuplicates) {
      errors.push('Duplicate passenger names are not allowed');
    }

    // Check for duplicate passport numbers if passport is mandatory
    if (customerData?.roomCardDetails?.isPassportMandatory) {
      const allPassports = new Set();
      let hasDuplicatePassports = false;

      passengerDetails.rooms.forEach(room => {
        [...room.adults, ...room.children].forEach(passenger => {
          if (passenger.passengerPassportNo && passenger.passengerPassportNo.trim()) {
            const passportNo = passenger.passengerPassportNo.trim().toUpperCase();
            if (allPassports.has(passportNo)) {
              hasDuplicatePassports = true;
            } else {
              allPassports.add(passportNo);
            }
          }
        });
      });

      if (hasDuplicatePassports) {
        errors.push('Duplicate passport numbers are not allowed');
      }
    }

    return errors;
  };
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Check session validity before submission - show modal if expired
    if (isExpired || !hasValidSession) {
      setShowSessionExpiredModal(true);
      return;
    }

    const errors = validateForm();
    if (errors.length > 0) {
      toast.error(errors.join('\n'));
      return;
    }

    try {
      setSubmitting(true);
      setTimeoutProgress(0);
      setShowTimeoutWarning(false);

      // Start timeout progress indicator
      const progressInterval = setInterval(() => {
        setTimeoutProgress(prev => {
          const newProgress = prev + (100 / 30); // 30 seconds total
          if (newProgress >= 50) { // Show warning at 15 seconds (50% progress)
            setShowTimeoutWarning(true);
          }
          return Math.min(newProgress, 100);
        });
      }, 1000);

      const response = await agentApiService.submitCustomerDetails(
        passengerDetails,
        customerData.reserveCode,
        roomBookingCode,
        timerKey
      );

      // Clear progress interval
      clearInterval(progressInterval);
      setTimeoutProgress(0);
      setShowTimeoutWarning(false);

      if (response.success) {
        toast.success('Passenger details saved successfully!');
        // Navigate to passenger summary page
        navigate(`/agent/dashboard/prebook/passenger-details?reserveCode=${customerData.reserveCode}&roomBookingCode=${roomBookingCode}&timerKey=${timerKey}`);
      } else {
        throw new Error(response.message || 'Failed to save passenger details');
      }
    } catch (error) {
      console.error('Error submitting passenger details:', error);
      setTimeoutProgress(0);
      setShowTimeoutWarning(false);
      toast.error(`Failed to save passenger details: ${error.message}`);
    } finally {
      setSubmitting(false);
    }
  };

  // Time options for arrival time dropdown
  const timeOptions = [
    { value: '', label: 'Please select' },
    { value: 'dont-know', label: "I don't know" },
    { value: '00-01', label: '00:00 – 01:00' },
    { value: '01-02', label: '01:00 – 02:00' },
    { value: '02-03', label: '02:00 – 03:00' },
    { value: '03-04', label: '03:00 – 04:00' },
    { value: '04-05', label: '04:00 – 05:00' },
    { value: '05-06', label: '05:00 – 06:00' },
    { value: '06-07', label: '06:00 – 07:00' },
    { value: '07-08', label: '07:00 – 08:00' },
    { value: '08-09', label: '08:00 – 09:00' },
    { value: '09-10', label: '09:00 – 10:00' },
    { value: '10-11', label: '10:00 – 11:00' },
    { value: '11-12', label: '11:00 – 12:00' },
    { value: '12-13', label: '12:00 – 13:00' },
    { value: '13-14', label: '13:00 – 14:00' },
    { value: '14-15', label: '14:00 – 15:00' },
    { value: '15-16', label: '15:00 – 16:00' },
    { value: '16-17', label: '16:00 – 17:00' },
    { value: '17-18', label: '17:00 – 18:00' },
    { value: '18-19', label: '18:00 – 19:00' },
    { value: '19-20', label: '19:00 – 20:00' },
    { value: '20-21', label: '20:00 – 21:00' },
    { value: '21-22', label: '21:00 – 22:00' },
    { value: '22-23', label: '22:00 – 23:00' },
    { value: '23-24', label: '23:00 – 24:00' }
  ];
  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center max-w-md mx-auto p-6">
          <LoadingSpinner message="Loading customer details..." />
          
          {/* Timeout Progress Indicator */}
          {timeoutProgress > 0 && (
            <div className="mt-6">
              <div className="w-full bg-gray-200 rounded-full h-2 mb-2">
                <div 
                  className={`h-2 rounded-full transition-all duration-1000 ${
                    showTimeoutWarning ? 'bg-yellow-500' : 'bg-blue-500'
                  }`}
                  style={{ width: `${timeoutProgress}%` }}
                ></div>
              </div>
              <p className={`text-sm ${showTimeoutWarning ? 'text-yellow-600' : 'text-gray-600'}`}>
                {showTimeoutWarning 
                  ? 'Taking longer than expected... Please wait.'
                  : 'Connecting to prebook service...'}
              </p>
              <p className="text-xs text-gray-500 mt-1">
                Timeout in {Math.max(0, 30 - Math.floor(timeoutProgress * 30 / 100))} seconds
              </p>
            </div>
          )}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center max-w-md mx-auto p-6">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <h3 className="text-xl font-bold text-gray-900 mb-2">Error Loading Customer Details</h3>
          <p className="text-gray-600 mb-6">{error}</p>
          <button 
            onClick={fetchCustomerData}
            className="bg-red-500 text-white px-6 py-2 rounded-lg font-medium hover:bg-red-600 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (!customerData) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <h3 className="text-xl font-bold text-gray-900 mb-2">No Customer Data Available</h3>
          <p className="text-gray-600">Unable to load customer details.</p>
        </div>
      </div>
    );
  }
  return (
    <div className="min-h-screen bg-gray-50">
      <BookingTimer timerKey={timerKey} expiryTime={expiryTime} />

      {/* Session Expired Warning */}
      {isExpired && (
        <div className="bg-red-50 border border-red-200 mx-4 mt-4 rounded-xl p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div className="ml-3 flex-1">
              <h3 className="text-lg font-semibold text-red-800">Booking Session Expired</h3>
              <p className="text-red-700 mt-1">
                Your booking session has expired. Please refresh the page to start a new session.
              </p>
              <button
                onClick={() => window.location.reload()}
                className="mt-3 bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg transition-colors"
              >
                Refresh Page
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white shadow-sm border-b sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button 
                onClick={() => window.close()}
                className="text-gray-500 hover:text-gray-700 p-1"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
              <div>
                <h1 className="text-xl font-bold text-gray-900">Hotel Book</h1>
                <p className="text-sm text-gray-600">Enter passenger information</p>
              </div>
            </div>
          </div>

          <div className="flex items-center space-x-4 mt-4">
            <div className="flex items-center">
              <div className="w-8 h-8 bg-blue-500 text-white rounded-full flex items-center justify-center text-sm font-medium">1</div>
              <span className="ml-2 text-sm font-medium text-blue-600">Your Selection</span>
            </div>
            <div className="flex-1 h-px bg-gray-300"></div>
            <div className="flex items-center">
              <div className="w-8 h-8 bg-blue-500 text-white rounded-full flex items-center justify-center text-sm font-medium">2</div>
              <span className="ml-2 text-sm font-medium text-blue-600">Your Details</span>
            </div>
            <div className="flex-1 h-px bg-gray-300"></div>
            <div className="flex items-center">
              <div className="w-8 h-8 bg-gray-300 text-gray-600 rounded-full flex items-center justify-center text-sm font-medium">3</div>
              <span className="ml-2 text-sm font-medium text-gray-500">Confirmation</span>
            </div>
          </div>
        </div>
      </div>
      <div className="max-w-6xl mx-auto px-4 py-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow-md p-6 sticky top-24">
              <div className="mb-4">
                <h3 className="text-lg font-bold text-gray-900 mb-4">
                  {customerData.roomCardDetails?.roomName?.[0] || 'Room'}
                </h3>
                
                <div className="flex items-center mb-3">
                  <svg className="w-4 h-4 text-gray-600 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M3 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V4zM3 10a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H4a1 1 0 01-1-1v-6zM14 9a1 1 0 00-1 1v6a1 1 0 001 1h2a1 1 0 001-1v-6a1 1 0 00-1-1h-2z" />
                  </svg>
                  <span className="text-sm text-gray-600">{customerData.roomCardDetails?.breakfastText}</span>
                </div>

                <div className="flex items-center mb-4">
                  <svg className="w-4 h-4 text-gray-600 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M13 6a3 3 0 11-6 0 3 3 0 016 0zM18 8a2 2 0 11-4 0 2 2 0 014 0zM14 15a4 4 0 00-8 0v3h8v-3z" />
                  </svg>
                  <span className="text-sm font-medium text-gray-900">
                    Rooms: {customerData.totalRooms} | Adults: {customerData.totalAdults} | Children: {customerData.totalChildren}
                  </span>
                </div>

                <div className="border-t pt-4">
                  <div className="flex justify-between items-center">
                    <span className="font-semibold">Total Price:</span>
                    <span className="text-lg font-bold text-green-600">
                      ₹{customerData.hotelDetails?.totalPrice ? 
                        new Intl.NumberFormat('en-IN').format(customerData.hotelDetails.totalPrice) : 'N/A'}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="lg:col-span-2">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="bg-white rounded-lg shadow-md p-6">
                <h3 className="text-lg font-bold text-gray-900 mb-4">Common Information</h3>
                
                <div className="space-y-4">
                  <div className="flex items-center space-x-4">
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={commonInfo.syncCommonInfo}
                        onChange={(e) => handleCommonInfoChange('syncCommonInfo', e.target.checked)}
                        className="mr-2"
                      />
                      Use this Mobile/Email/Country for all adults
                    </label>
                    
                    {customerData.roomCardDetails?.isPanMandatory && (
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={commonInfo.syncPanInfo}
                          onChange={(e) => handleCommonInfoChange('syncPanInfo', e.target.checked)}
                          className="mr-2"
                        />
                        Use this PAN for all adults
                      </label>
                    )}
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    {customerData.roomCardDetails?.isPanMandatory && (
                      <input
                        type="text"
                        placeholder="PAN Number"
                        value={commonInfo.pan}
                        onChange={(e) => handleCommonInfoChange('pan', e.target.value)}
                        className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    )}
                    <input
                      type="text"
                      placeholder="Mobile Number"
                      value={commonInfo.mobile}
                      onChange={(e) => handleCommonInfoChange('mobile', e.target.value)}
                      className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <input
                      type="email"
                      placeholder="Email"
                      value={commonInfo.email}
                      onChange={(e) => handleCommonInfoChange('email', e.target.value)}
                      className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <input
                      type="text"
                      placeholder="Country"
                      value={commonInfo.country}
                      onChange={(e) => handleCommonInfoChange('country', e.target.value)}
                      className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>
              </div>

              {/* Passenger Details Forms */}
              {passengerDetails.rooms.map((room, roomIndex) => (
                <div key={roomIndex} className="bg-white rounded-lg shadow-md p-6">
                  <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                    <svg className="w-5 h-5 text-blue-600 mr-2" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M10.394 2.08a1 1 0 00-.788 0l-7 3a1 1 0 000 1.84L5.25 8.051a.999.999 0 01.356-.257l4-1.714a1 1 0 11.788 1.838L7.667 9.088l1.94.831a1 1 0 00.787 0l7-3a1 1 0 000-1.838l-7-3z" />
                    </svg>
                    Room {room.roomNumber} Details
                  </h3>

                  {/* Adults Section */}
                  <div className="mb-6">
                    <h4 className="text-md font-semibold text-green-600 mb-3 flex items-center">
                      <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                      </svg>
                      Adults ({room.adults.length})
                    </h4>
                    
                    {room.adults.map((adult, adultIndex) => (
                      <div key={adultIndex} className="mb-4 p-4 bg-green-50 rounded-lg border border-green-200">
                        <h5 className="font-medium text-gray-900 mb-3">Adult {adultIndex + 1}</h5>
                        
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-3">
                          <select
                            value={adult.passengerTitle}
                            onChange={(e) => handleInputChange(roomIndex, 'adults', adultIndex, 'passengerTitle', e.target.value)}
                            className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          >
                            <option value="Mr.">Mr.</option>
                            <option value="Mrs.">Mrs.</option>
                            <option value="Ms.">Ms.</option>
                            <option value="Dr.">Dr.</option>
                          </select>
                          
                          <input
                            type="text"
                            placeholder="First Name *"
                            value={adult.passengerFirstName}
                            onChange={(e) => handleInputChange(roomIndex, 'adults', adultIndex, 'passengerFirstName', e.target.value)}
                            className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            required
                          />
                          
                          <input
                            type="text"
                            placeholder="Last Name *"
                            value={adult.passengerLastName}
                            onChange={(e) => handleInputChange(roomIndex, 'adults', adultIndex, 'passengerLastName', e.target.value)}
                            className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            required
                          />
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-3">
                          <input
                            type="tel"
                            placeholder="Phone Number *"
                            value={adult.passengerPhoneNumber}
                            onChange={(e) => handleInputChange(roomIndex, 'adults', adultIndex, 'passengerPhoneNumber', e.target.value)}
                            className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            required
                          />
                          
                          <input
                            type="email"
                            placeholder="Email *"
                            value={adult.passengerEmail}
                            onChange={(e) => handleInputChange(roomIndex, 'adults', adultIndex, 'passengerEmail', e.target.value)}
                            className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            required
                          />
                          
                          <input
                            type="text"
                            placeholder="Country *"
                            value={adult.passengerCountry}
                            onChange={(e) => handleInputChange(roomIndex, 'adults', adultIndex, 'passengerCountry', e.target.value)}
                            className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            required
                          />
                        </div>

                        {customerData.roomCardDetails?.isPanMandatory && (
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-3">
                            <input
                              type="text"
                              placeholder="PAN Card Number"
                              value={adult.passengerPANCardNumber}
                              onChange={(e) => handleInputChange(roomIndex, 'adults', adultIndex, 'passengerPANCardNumber', e.target.value)}
                              className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                          </div>
                        )}

                        {customerData.roomCardDetails?.isPassportMandatory && (
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <input
                              type="text"
                              placeholder="Passport Number"
                              value={adult.passengerPassportNo}
                              onChange={(e) => handleInputChange(roomIndex, 'adults', adultIndex, 'passengerPassportNo', e.target.value)}
                              className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                            
                            <input
                              type="date"
                              placeholder="Passport Issue Date"
                              value={adult.passengerPassportIssueDate}
                              onChange={(e) => handleInputChange(roomIndex, 'adults', adultIndex, 'passengerPassportIssueDate', e.target.value)}
                              className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                            
                            <input
                              type="date"
                              placeholder="Passport Expiry Date"
                              value={adult.passengerPassportExpDate}
                              onChange={(e) => handleInputChange(roomIndex, 'adults', adultIndex, 'passengerPassportExpDate', e.target.value)}
                              className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                          </div>
                        )}
                      </div>
                    ))}
                  </div>

                  {/* Children Section */}
                  {room.children.length > 0 && (
                    <div className="mb-4">
                      <h4 className="text-md font-semibold text-yellow-600 mb-3 flex items-center">
                        <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 2a4 4 0 00-4 4v1H5a1 1 0 00-.994.89l-1 9A1 1 0 004 18h12a1 1 0 00.994-1.11l-1-9A1 1 0 0015 7h-1V6a4 4 0 00-4-4zM8 6V5a2 2 0 114 0v1H8zm2 6a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
                        </svg>
                        Children ({room.children.length})
                      </h4>
                      
                      {room.children.map((child, childIndex) => (
                        <div key={childIndex} className="mb-4 p-4 bg-yellow-50 rounded-lg border border-yellow-200">
                          <h5 className="font-medium text-gray-900 mb-3">Child {childIndex + 1} (Age: {child.passengerAge})</h5>
                          
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-3">
                            <select
                              value={child.passengerTitle}
                              onChange={(e) => handleInputChange(roomIndex, 'children', childIndex, 'passengerTitle', e.target.value)}
                              className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            >
                              <option value="Master">Master</option>
                              <option value="Miss">Miss</option>
                            </select>
                            
                            <input
                              type="text"
                              placeholder="First Name *"
                              value={child.passengerFirstName}
                              onChange={(e) => handleInputChange(roomIndex, 'children', childIndex, 'passengerFirstName', e.target.value)}
                              className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                              required
                            />
                            
                            <input
                              type="text"
                              placeholder="Last Name *"
                              value={child.passengerLastName}
                              onChange={(e) => handleInputChange(roomIndex, 'children', childIndex, 'passengerLastName', e.target.value)}
                              className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                              required
                            />
                          </div>

                          {customerData.roomCardDetails?.isPassportMandatory && (
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                              <input
                                type="text"
                                placeholder="Passport Number"
                                value={child.passengerPassportNo}
                                onChange={(e) => handleInputChange(roomIndex, 'children', childIndex, 'passengerPassportNo', e.target.value)}
                                className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                              />
                              
                              <input
                                type="date"
                                placeholder="Passport Issue Date"
                                value={child.passengerPassportIssueDate}
                                onChange={(e) => handleInputChange(roomIndex, 'children', childIndex, 'passengerPassportIssueDate', e.target.value)}
                                className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                              />
                              
                              <input
                                type="date"
                                placeholder="Passport Expiry Date"
                                value={child.passengerPassportExpDate}
                                onChange={(e) => handleInputChange(roomIndex, 'children', childIndex, 'passengerPassportExpDate', e.target.value)}
                                className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                              />
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}

              {/* GST Information */}
              <div className="bg-white rounded-lg shadow-md p-6">
                <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                  <svg className="w-5 h-5 text-purple-600 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M4 4a2 2 0 00-2 2v1h16V6a2 2 0 00-2-2H4zM18 9H2v5a2 2 0 002 2h12a2 2 0 002-2V9zM4 13a1 1 0 011-1h1a1 1 0 110 2H5a1 1 0 01-1-1zm5-1a1 1 0 100 2h1a1 1 0 100-2H9z" />
                  </svg>
                  GST Information (Optional)
                </h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <input
                    type="text"
                    placeholder="GST Number"
                    value={passengerDetails.gstInfo.gstNumber}
                    onChange={(e) => handleGstChange('gstNumber', e.target.value)}
                    className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  
                  <input
                    type="text"
                    placeholder="Company Name"
                    value={passengerDetails.gstInfo.gstCompanyName}
                    onChange={(e) => handleGstChange('gstCompanyName', e.target.value)}
                    className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  
                  <input
                    type="text"
                    placeholder="Company Address"
                    value={passengerDetails.gstInfo.gstCompanyAddress}
                    onChange={(e) => handleGstChange('gstCompanyAddress', e.target.value)}
                    className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  
                  <input
                    type="tel"
                    placeholder="Company Contact Number"
                    value={passengerDetails.gstInfo.gstCompanyContactNumber}
                    onChange={(e) => handleGstChange('gstCompanyContactNumber', e.target.value)}
                    className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  
                  <input
                    type="email"
                    placeholder="Company Email"
                    value={passengerDetails.gstInfo.gstCompanyEmail}
                    onChange={(e) => handleGstChange('gstCompanyEmail', e.target.value)}
                    className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 md:col-span-2"
                  />
                </div>
              </div>

              {/* Special Requests */}
              <div className="bg-white rounded-lg shadow-md p-6">
                <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                  <svg className="w-5 h-5 text-orange-600 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 13V5a2 2 0 00-2-2H4a2 2 0 00-2 2v8a2 2 0 002 2h3l3 3 3-3h3a2 2 0 002-2zM5 7a1 1 0 011-1h8a1 1 0 110 2H6a1 1 0 01-1-1zm1 3a1 1 0 100 2h3a1 1 0 100-2H6z" clipRule="evenodd" />
                </svg>
                  Special Requests (Optional)
                </h3>
                
                <div className="space-y-4">
                  <textarea
                    placeholder="Any special requests or requirements..."
                    value={specialRequests.requestText}
                    onChange={(e) => handleSpecialRequestChange('requestText', e.target.value)}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  
                  <div className="flex items-center space-x-6">
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={specialRequests.closeRooms}
                        onChange={(e) => handleSpecialRequestChange('closeRooms', e.target.checked)}
                        className="mr-2"
                      />
                      Request rooms close to each other
                    </label>
                    
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={specialRequests.freeParking}
                        onChange={(e) => handleSpecialRequestChange('freeParking', e.target.checked)}
                        className="mr-2"
                      />
                      Free parking (if available)
                    </label>
                  </div>
                </div>
              </div>

              {/* Arrival Information */}
              <div className="bg-white rounded-lg shadow-md p-6">
                <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                  <svg className="w-5 h-5 text-indigo-600 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                  </svg>
                  Arrival Information (Optional)
                </h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <input
                    type="date"
                    placeholder="Arrival Date"
                    value={arrivalInfo.arrivalDate}
                    onChange={(e) => handleArrivalInfoChange('arrivalDate', e.target.value)}
                    className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  
                  <select
                    value={arrivalInfo.arrivalTime}
                    onChange={(e) => handleArrivalInfoChange('arrivalTime', e.target.value)}
                    className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    {timeOptions.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="flex justify-between items-center">
                <button
                  type="button"
                  onClick={() => window.close()}
                  className="px-6 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                
                <button
                  type="submit"
                  disabled={submitting || isExpired || !hasValidSession}
                  className={`px-6 py-2 rounded-md transition-colors ${
                    submitting || isExpired || !hasValidSession
                      ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      : 'bg-blue-600 text-white hover:bg-blue-700'
                  }`}
                >
                  {submitting ? (
                    <div className="flex items-center space-x-2">
                      <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      <span>Saving...</span>
                      {showTimeoutWarning && (
                        <span className="text-yellow-200 text-xs">
                          ({Math.max(0, 30 - Math.floor(timeoutProgress * 30 / 100))}s)
                        </span>
                      )}
                    </div>
                  ) : 'Confirm Passengers'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      {/* Session Expired Modal */}
      <SessionExpiredModal
        isOpen={showSessionExpiredModal}
        onClose={() => setShowSessionExpiredModal(false)}
      />
    </div>
  );
};

export default AgentCustomerDetailsPage;